import { useState, useEffect, useRef, useCallback } from 'react';
import { Package, User, ShoppingCart, Plus, Check, AlertCircle, Users, Warehouse, Store, MapPin, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { getProductByBarcode, getCategories } from '../lib/api';
import { BarcodeScanner } from './BarcodeScanner';

const QuickSaleModal = ({ isOpen, onClose, scannedBarcode: initialBarcode, privacyMode, onComplete }) => {
    // Step: 1 = product info, 2 = destination choice, 3 = customer/location details
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showCameraScanner, setShowCameraScanner] = useState(false);
    
    // Local barcode state (can be changed for next scan)
    const [barcode, setBarcode] = useState('');
    const [needsScan, setNeedsScan] = useState(false);
    const barcodeInputRef = useRef(null);
    
    // Destination: 'customer', 'depozit', 'magazin'
    const [destination, setDestination] = useState(null);
    
    // Product state
    const [product, setProduct] = useState(null);
    const [isNewProduct, setIsNewProduct] = useState(false);
    const [productName, setProductName] = useState('');
    const [productCategory, setProductCategory] = useState('');
    const [productPrice, setProductPrice] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [finalPrice, setFinalPrice] = useState('');
    
    // Categories
    const [categories, setCategories] = useState([]);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const categoryInputRef = useRef(null);
    
    // Product name suggestions
    const [productNames, setProductNames] = useState([]);
    const [showProductNameDropdown, setShowProductNameDropdown] = useState(false);
    const productNameInputRef = useRef(null);
    
    // Customer state
    const [customerId, setCustomerId] = useState('');
    const [existingCustomer, setExistingCustomer] = useState(null);
    const [isNewCustomer, setIsNewCustomer] = useState(false);
    const [isSafeCustomer, setIsSafeCustomer] = useState(false);
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    
    // Customers with open orders (simplified - just IDs and totals)
    const [customersWithOrders, setCustomersWithOrders] = useState([]);
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    
    // Depozit location
    const [depozitLocation, setDepozitLocation] = useState('P01');
    const [existingLocations, setExistingLocations] = useState([]);
    
    // Load categories on mount
    useEffect(() => {
        loadCategories();
        loadProductNames();
    }, []);
    
    // When initial barcode changes, set local barcode and lookup product
    useEffect(() => {
        if (isOpen && initialBarcode) {
            setBarcode(initialBarcode);
            setNeedsScan(false);
            lookupProduct(initialBarcode);
        }
    }, [isOpen, initialBarcode]);
    
    // Reset when modal closes
    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);
    
    // Focus barcode input when needsScan is true
    useEffect(() => {
        if (needsScan && barcodeInputRef.current) {
            barcodeInputRef.current.focus();
        }
    }, [needsScan]);
    
    // Close category dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showCategoryDropdown && !e.target.closest('[data-category-dropdown]')) {
                setShowCategoryDropdown(false);
            }
            if (showProductNameDropdown && !e.target.closest('[data-product-name-dropdown]')) {
                setShowProductNameDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCategoryDropdown, showProductNameDropdown]);
    
    const loadCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };
    
    const loadProductNames = async () => {
        try {
            const response = await api.get('/products/names');
            setProductNames(response.data);
        } catch (err) {
            console.error('Error loading product names:', err);
        }
    };
    
    const resetForm = () => {
        setStep(1);
        setBarcode('');
        setNeedsScan(false);
        setDestination(null);
        setProduct(null);
        setIsNewProduct(false);
        setProductName('');
        setProductCategory('');
        setProductPrice('');
        setQuantity('1');
        setFinalPrice('');
        setCustomerId('');
        setExistingCustomer(null);
        setIsNewCustomer(false);
        setCustomerName('');
        setCustomerPhone('');
        setCustomerAddress('');
        setLoading(false);
        setCustomersWithOrders([]);
        setShowNewCustomerForm(false);
        setDepozitLocation('P01');
        setExistingLocations([]);
    };
    
    // Reset for next product scan - keeps modal open
    const resetForNextScan = () => {
        setStep(1);
        setBarcode('');
        setNeedsScan(true); // Show barcode input for next scan
        setDestination(null);
        setProduct(null);
        setIsNewProduct(false);
        setProductName('');
        setProductCategory('');
        setProductPrice('');
        setQuantity('1');
        setFinalPrice('');
        // Reset customer data
        setCustomersWithOrders([]);
        setShowNewCustomerForm(false);
        setIsSafeCustomer(false);
        setLoading(false);
        setShowCameraScanner(false);
        // Reload categories to include any new ones
        loadCategories();
    };
    
    // Handle camera scan result
    const handleCameraScan = async (scannedCode) => {
        setShowCameraScanner(false);
        setBarcode(scannedCode);
        setNeedsScan(false);
        await lookupProduct(scannedCode);
    };
    
    // Manual barcode search
    const handleBarcodeSearch = async () => {
        if (!barcode.trim()) return;
        setNeedsScan(false);
        await lookupProduct(barcode.trim());
    };
    
    const handleBarcodeKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleBarcodeSearch();
        }
    };
    
    // Fast load - just get customers with pending orders count (no full data)
    const loadCustomersWithOrders = async () => {
        try {
            const response = await api.get(`/customers/with-orders?safe_mode=${privacyMode}`);
            setCustomersWithOrders(response.data || []);
        } catch (err) {
            console.error('Error loading customers:', err);
            setCustomersWithOrders([]);
        }
    };
    
    // Load existing depozit locations
    const loadDepozitLocations = async () => {
        try {
            const response = await api.get('/depozit/locations');
            setExistingLocations(response.data || []);
        } catch (err) {
            console.error('Error loading locations:', err);
        }
    };
    
    const lookupProduct = async (barcode) => {
        setLoading(true);
        try {
            const response = await getProductByBarcode(barcode);
            if (response.data) {
                const productData = response.data;
                setProduct(productData);
                setProductName(productData.name);
                setProductCategory(productData.category || '');
                setProductPrice(productData.price?.toString() || '0');
                setIsNewProduct(false);
                
                // Try to get last sale price for this product
                try {
                    const lastPriceRes = await api.get(`/products/${productData.id}/last-price`);
                    if (lastPriceRes.data && lastPriceRes.data.last_price) {
                        setFinalPrice(lastPriceRes.data.last_price.toString());
                    } else {
                        setFinalPrice(productData.price?.toString() || '0');
                    }
                } catch {
                    setFinalPrice(productData.price?.toString() || '0');
                }
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setIsNewProduct(true);
                setProductName('');
                setProductCategory('');
                setProductPrice('');
                setFinalPrice('');
            } else {
                toast.error('Eroare la căutarea produsului');
            }
        } finally {
            setLoading(false);
        }
    };
    
    // Debounce timer ref
    const lookupTimerRef = useRef(null);
    
    const lookupCustomer = useCallback(async (id) => {
        if (!id || id.length < 2) {
            setExistingCustomer(null);
            setIsNewCustomer(false);
            return;
        }
        
        try {
            const response = await api.get(`/customers/${id}`);
            if (response.data) {
                setExistingCustomer(response.data);
                setIsNewCustomer(false);
                setCustomerName(response.data.name || '');
                setCustomerPhone(response.data.phone || '');
                setCustomerAddress(response.data.address || '');
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setExistingCustomer(null);
                setIsNewCustomer(true);
                setCustomerName('');
                setCustomerPhone('');
                setCustomerAddress('');
            }
        }
    }, []);
    
    const handleCustomerIdChange = (value) => {
        setCustomerId(value);
        
        // Debounce the lookup to prevent too many API calls
        if (lookupTimerRef.current) {
            clearTimeout(lookupTimerRef.current);
        }
        lookupTimerRef.current = setTimeout(() => {
            lookupCustomer(value);
        }, 300);
    };
    
    const handleSelectCustomer = (customer) => {
        setCustomerId(customer.customer_id);
        setExistingCustomer(customer);
        setIsNewCustomer(false);
        setShowNewCustomerForm(false);
    };
    
    const handleShowNewCustomerForm = () => {
        setShowNewCustomerForm(true);
        setCustomerId('');
        setExistingCustomer(null);
        setIsNewCustomer(false);
    };
    
    // Handle Enter key in product form to go to next step
    const handleProductFormKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleNextStep();
        }
    };
    
    const handleNextStep = () => {
        if (!productName.trim()) {
            toast.error('Introdu numele produsului');
            return;
        }
        if (destination === 'customer' && (!finalPrice || parseFloat(finalPrice) < 0)) {
            toast.error('Introdu un preț valid');
            return;
        }
        // Pre-load data for step 2
        loadCustomersWithOrders();
        loadDepozitLocations();
        setStep(2);
    };
    
    const handleSelectDestination = async (dest) => {
        setDestination(dest);
        
        if (dest === 'magazin') {
            // Direct add to magazin without confirmation
            setLoading(true);
            try {
                const qty = parseInt(quantity) || 1;
                let productId = product?.id;
                
                if (isNewProduct) {
                    const createRes = await api.post('/products', {
                        name: productName,
                        barcode: barcode,
                        price: parseFloat(productPrice) || 0,
                        category: productCategory
                    });
                    productId = createRes.data.id;
                }
                
                await api.post('/store', {
                    product_id: productId,
                    quantity: qty
                });
                toast.success(`${productName} x${qty} → Magazin`);
                
                if (onComplete) onComplete();
                resetForNextScan();
                setShowCameraScanner(true);
                
            } catch (err) {
                console.error('Error:', err);
                toast.error(err.response?.data?.detail || 'Eroare la adăugare');
            } finally {
                setLoading(false);
            }
        } else if (dest === 'depozit') {
            loadDepozitLocations();
            setStep(3);
        } else if (dest === 'customer') {
            loadCustomersWithOrders();
            setStep(3);
        }
    };
    
    // Direct add to existing customer (skips confirmation)
    const handleAddToExistingCustomer = async (customer) => {
        setDestination('customer');
        setCustomerId(customer.customer_id);
        setExistingCustomer(customer);
        setIsNewCustomer(false);
        
        setLoading(true);
        try {
            const qty = parseInt(quantity) || 1;
            let productId = product?.id;
            
            if (isNewProduct) {
                const createRes = await api.post('/products', {
                    name: productName,
                    barcode: barcode,
                    price: parseFloat(productPrice) || 0,
                    category: productCategory
                });
                productId = createRes.data.id;
            }
            
            const requestData = {
                quantity: qty,
                final_price: parseFloat(finalPrice) || 0
            };
            
            if (isNewProduct) {
                requestData.new_product_name = productName;
                requestData.new_product_price = parseFloat(productPrice) || 0;
                requestData.new_product_category = productCategory;
                requestData.barcode = barcode;
            } else {
                requestData.product_id = productId;
            }
            
            await api.post(`/customers/${customer.customer_id}/add-product`, requestData);
            toast.success(`${productName} x${qty} → #${customer.customer_id}`);
            
            if (onComplete) onComplete();
            resetForNextScan();
            setShowCameraScanner(true);
            
        } catch (err) {
            console.error('Error:', err);
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
        } finally {
            setLoading(false);
        }
    };
    
    const handleComplete = async () => {
        setLoading(true);
        try {
            const qty = parseInt(quantity) || 1;
            
            // First, ensure product exists
            let productId = product?.id;
            if (isNewProduct) {
                const createRes = await api.post('/products', {
                    name: productName,
                    barcode: barcode,
                    price: parseFloat(productPrice) || 0,
                    category: productCategory
                });
                productId = createRes.data.id;
            }
            
            if (destination === 'customer') {
                // Add to customer order
                if (!customerId.trim()) {
                    toast.error('Introdu ID-ul clientului');
                    setLoading(false);
                    return;
                }
                
                if (isNewCustomer) {
                    await api.post('/customers', {
                        customer_id: customerId,
                        name: customerName,
                        phone: customerPhone,
                        address: customerAddress
                    });
                }
                
                const requestData = {
                    quantity: qty,
                    final_price: parseFloat(finalPrice) || 0
                };
                
                if (isNewProduct) {
                    requestData.new_product_name = productName;
                    requestData.new_product_price = parseFloat(productPrice) || 0;
                    requestData.new_product_category = productCategory;
                    requestData.barcode = barcode;
                } else {
                    requestData.product_id = productId;
                }
                
                await api.post(`/customers/${customerId}/add-product`, requestData);
                toast.success(`Adăugat ${productName} x${qty} la #${customerId}`);
                
            } else if (destination === 'depozit') {
                // Add to depozit
                await api.post('/depozit', {
                    product_id: productId,
                    quantity: qty,
                    location: depozitLocation
                });
                toast.success(`Adăugat ${productName} x${qty} în Depozit (${depozitLocation})`);
                
            } else if (destination === 'magazin') {
                // Add to magazin
                await api.post('/store', {
                    product_id: productId,
                    quantity: qty
                });
                toast.success(`Adăugat ${productName} x${qty} în Magazin`);
            }
            
            if (onComplete) {
                onComplete();
            }
            
            // Reset for next scan and auto-open camera
            resetForNextScan();
            setShowCameraScanner(true);
            
        } catch (err) {
            console.error('Error:', err);
            toast.error(err.response?.data?.detail || 'Eroare la procesare');
        } finally {
            setLoading(false);
        }
    };
    
    // Filter categories based on input
    const filteredCategories = categories.filter(cat => 
        cat.toLowerCase().includes(productCategory.toLowerCase())
    );
    
    if (!isOpen) return null;
    
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package className="text-rose-500" size={20} />
                        Procesare Produs
                    </DialogTitle>
                </DialogHeader>
                
                {/* Progress indicator */}
                <div className="flex items-center gap-2 mb-4">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-rose-500 text-white' : 'bg-zinc-200'}`}>
                        <Package size={16} />
                    </div>
                    <div className={`flex-1 h-1 ${step >= 2 ? 'bg-rose-500' : 'bg-zinc-200'}`} />
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-rose-500 text-white' : 'bg-zinc-200'}`}>
                        <MapPin size={16} />
                    </div>
                    <div className={`flex-1 h-1 ${step >= 3 ? 'bg-rose-500' : 'bg-zinc-200'}`} />
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 3 ? 'bg-rose-500 text-white' : 'bg-zinc-200'}`}>
                        <Check size={16} />
                    </div>
                </div>
                
                {loading && step === 1 && !product && !isNewProduct && !needsScan ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500" />
                    </div>
                ) : step === 1 && needsScan ? (
                    /* Step 1: Scan New Barcode */
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-rose-600 bg-rose-50 rounded-lg p-3">
                            <Check size={18} />
                            <span className="text-sm">Produs adăugat! Scanează următorul sau închide.</span>
                        </div>
                        
                        <div>
                            <Label>Cod de bare</Label>
                            <div className="flex gap-2">
                                <Input
                                    ref={barcodeInputRef}
                                    value={barcode}
                                    onChange={(e) => setBarcode(e.target.value)}
                                    onKeyDown={handleBarcodeKeyDown}
                                    placeholder="Scanează sau introdu codul"
                                    autoFocus
                                    className="flex-1"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowCameraScanner(true)}
                                    className="px-3"
                                    title="Scanează cu camera"
                                >
                                    <Camera size={20} />
                                </Button>
                            </div>
                        </div>
                        
                        <Button 
                            onClick={handleBarcodeSearch}
                            disabled={loading || !barcode.trim()}
                            className="w-full bg-rose-500 hover:bg-rose-600"
                        >
                            {loading ? 'Se caută...' : 'Caută Produs'}
                        </Button>
                    </div>
                ) : step === 1 ? (
                    /* Step 1: Product Info */
                    <div className="space-y-4">
                        <div className="bg-zinc-50 rounded-lg p-3">
                            <p className="text-sm text-zinc-500">Cod scanat:</p>
                            <p className="font-mono font-semibold">{barcode}</p>
                        </div>
                        
                        {isNewProduct && (
                            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-3">
                                <AlertCircle size={18} />
                                <span className="text-sm">Produs nou - completează datele</span>
                            </div>
                        )}
                        
                        {!isNewProduct && product && (
                            <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-lg p-3">
                                <Check size={18} />
                                <span className="text-sm">Produs găsit în sistem</span>
                            </div>
                        )}
                        
                        <div data-product-name-dropdown className="relative">
                            <Label>Nume Produs *</Label>
                            <Input
                                ref={productNameInputRef}
                                value={productName}
                                onChange={(e) => {
                                    setProductName(e.target.value);
                                    if (isNewProduct) {
                                        setShowProductNameDropdown(true);
                                    }
                                }}
                                onFocus={() => isNewProduct && setShowProductNameDropdown(true)}
                                onKeyDown={handleProductFormKeyDown}
                                placeholder="Denumire produs"
                                disabled={!isNewProduct}
                                className={!isNewProduct ? 'bg-zinc-100' : ''}
                                autoFocus={isNewProduct}
                            />
                            {showProductNameDropdown && isNewProduct && (
                                (() => {
                                    const filtered = productNames.filter(p => 
                                        p.name.toLowerCase().includes(productName.toLowerCase())
                                    ).slice(0, 10);
                                    return filtered.length > 0 && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                            {filtered.map((p, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    className="w-full px-3 py-2 text-left hover:bg-zinc-100 text-sm flex justify-between items-center"
                                                    onClick={() => {
                                                        setProductName(p.name);
                                                        if (p.price) setFinalPrice(p.price.toString());
                                                        if (p.price) setProductPrice(p.price.toString());
                                                        if (p.category) setProductCategory(p.category);
                                                        setShowProductNameDropdown(false);
                                                    }}
                                                >
                                                    <span>{p.name}</span>
                                                    {p.price && <span className="text-zinc-400 text-xs">{p.price} RON</span>}
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                        
                        <div data-category-dropdown className="relative">
                            <Label>Categorie</Label>
                            <Input
                                ref={categoryInputRef}
                                value={productCategory}
                                onChange={(e) => {
                                    setProductCategory(e.target.value);
                                    setShowCategoryDropdown(true);
                                }}
                                onFocus={() => setShowCategoryDropdown(true)}
                                placeholder="Selectează sau scrie categorie"
                                disabled={!isNewProduct}
                                className={!isNewProduct ? 'bg-zinc-100' : ''}
                            />
                            {showCategoryDropdown && isNewProduct && filteredCategories.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {filteredCategories.map((cat) => (
                                        <button
                                            key={cat}
                                            type="button"
                                            className="w-full px-3 py-2 text-left hover:bg-zinc-100 text-sm"
                                            onClick={() => {
                                                setProductCategory(cat);
                                                setShowCategoryDropdown(false);
                                            }}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Cantitate *</Label>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setQuantity(prev => Math.max(1, parseInt(prev) - 1).toString())}
                                        className="w-10 h-10 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-600"
                                    >
                                        −
                                    </button>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        className="text-center"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setQuantity(prev => (parseInt(prev) + 1).toString())}
                                        className="w-10 h-10 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-600"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            <div>
                                <Label>Preț (RON)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={finalPrice}
                                    onChange={(e) => setFinalPrice(e.target.value)}
                                    placeholder="Preț"
                                />
                            </div>
                        </div>
                        
                        {isNewProduct && (
                            <div>
                                <Label>Preț Catalog (RON)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={productPrice}
                                    onChange={(e) => {
                                        setProductPrice(e.target.value);
                                        if (!finalPrice) setFinalPrice(e.target.value);
                                    }}
                                    placeholder="Preț de referință"
                                />
                            </div>
                        )}
                        
                        <Button 
                            onClick={handleNextStep} 
                            className="w-full bg-rose-500 hover:bg-rose-600"
                        >
                            Alege Destinația
                        </Button>
                    </div>
                ) : step === 2 ? (
                    /* Step 2: Destination Choice */
                    <div className="space-y-4">
                        <div className="bg-zinc-50 rounded-lg p-3">
                            <p className="text-sm text-zinc-500">Produs:</p>
                            <p className="font-semibold">{productName} x{quantity}</p>
                        </div>
                        
                        <p className="text-sm text-zinc-600 text-center">Unde trimiți produsul?</p>
                        
                        {/* Quick select customers with open orders */}
                        {customersWithOrders.length > 0 && (
                            <div>
                                <Label className="text-xs text-zinc-500 mb-2 block">Comenzi deschise:</Label>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {customersWithOrders.map((customer) => (
                                        <button
                                            key={customer.customer_id}
                                            type="button"
                                            onClick={() => handleAddToExistingCustomer(customer)}
                                            className="w-12 h-12 rounded-full bg-violet-100 hover:bg-violet-200 flex items-center justify-center text-sm font-bold text-violet-700 border-2 border-violet-200 hover:border-violet-400 transition-colors"
                                            title={`${customer.order_total?.toFixed(0) || 0} RON`}
                                        >
                                            {customer.customer_id}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <div className="grid grid-cols-1 gap-2">
                            {/* New Customer */}
                            <Button
                                variant="outline"
                                className="h-12 flex items-center justify-start gap-3 px-4 hover:bg-violet-50 hover:border-violet-300"
                                onClick={() => {
                                    setDestination('customer');
                                    setShowNewCustomerForm(true);
                                    setCustomerId('');
                                    setExistingCustomer(null);
                                    setIsNewCustomer(true);
                                    setStep(3);
                                }}
                            >
                                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                                    <Plus className="text-violet-600" size={16} />
                                </div>
                                <p className="font-medium">Client Nou</p>
                            </Button>
                            
                            {/* Depozit */}
                            <Button
                                variant="outline"
                                className="h-12 flex items-center justify-start gap-3 px-4 hover:bg-amber-50 hover:border-amber-300"
                                onClick={() => handleSelectDestination('depozit')}
                            >
                                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                                    <Warehouse className="text-amber-600" size={16} />
                                </div>
                                <p className="font-medium">Depozit</p>
                            </Button>
                            
                            {/* Magazin */}
                            <Button
                                variant="outline"
                                className="h-12 flex items-center justify-start gap-3 px-4 hover:bg-emerald-50 hover:border-emerald-300"
                                onClick={() => handleSelectDestination('magazin')}
                            >
                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <Store className="text-emerald-600" size={16} />
                                </div>
                                <p className="font-medium">Magazin</p>
                            </Button>
                        </div>
                        
                        <Button 
                            variant="outline" 
                            onClick={() => setStep(1)}
                            className="w-full"
                        >
                            Înapoi
                        </Button>
                    </div>
                ) : step === 3 && destination === 'customer' ? (
                    /* Step 3: Customer Selection */
                    <div className="space-y-4">
                        <div className="bg-zinc-50 rounded-lg p-3">
                            <p className="text-sm text-zinc-500">Produs:</p>
                            <p className="font-semibold">{productName} x{quantity} = {(parseFloat(finalPrice || 0) * parseInt(quantity)).toFixed(2)} RON</p>
                        </div>
                        
                        {/* Customers with open orders - simplified view */}
                        {!showNewCustomerForm && customersWithOrders.length > 0 && !existingCustomer && (
                            <div>
                                <Label className="flex items-center gap-2 mb-2">
                                    <Users size={16} />
                                    Comenzi deschise
                                </Label>
                                <div className="max-h-32 overflow-y-auto border border-zinc-200 rounded-lg divide-y">
                                    {customersWithOrders.map((customer) => (
                                        <button
                                            key={customer.customer_id}
                                            type="button"
                                            onClick={() => handleSelectCustomer(customer)}
                                            className="w-full px-3 py-2 text-left hover:bg-zinc-50 flex justify-between items-center"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-sm font-bold text-violet-700">
                                                    {customer.customer_id}
                                                </span>
                                            </div>
                                            <span className="font-medium text-rose-600">{customer.order_total?.toFixed(0) || 0} RON</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Add new customer button */}
                        {!showNewCustomerForm && !existingCustomer && (
                            <Button 
                                variant="outline" 
                                onClick={handleShowNewCustomerForm}
                                className="w-full border-dashed"
                            >
                                <Plus size={16} className="mr-2" />
                                Client Nou
                            </Button>
                        )}
                        
                        {/* New customer form or manual ID entry */}
                        {(showNewCustomerForm || existingCustomer) && (
                            <>
                                <div>
                                    <Label>ID Client *</Label>
                                    <Input
                                        value={customerId}
                                        onChange={(e) => handleCustomerIdChange(e.target.value)}
                                        placeholder="ex: 1234"
                                        autoFocus={showNewCustomerForm}
                                        disabled={!!existingCustomer}
                                    />
                                </div>
                                
                                {existingCustomer && (
                                    <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-lg p-3">
                                        <Check size={18} />
                                        <div className="flex-1">
                                            <span className="text-sm font-medium">Client #{existingCustomer.customer_id}</span>
                                        </div>
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => {
                                                setExistingCustomer(null);
                                                setCustomerId('');
                                                setShowNewCustomerForm(false);
                                            }}
                                            className="text-zinc-500 hover:text-zinc-700"
                                        >
                                            Schimbă
                                        </Button>
                                    </div>
                                )}
                                
                                {isNewCustomer && customerId.length >= 2 && (
                                    <>
                                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-3">
                                            <Plus size={18} />
                                            <span className="text-sm">Client nou</span>
                                        </div>
                                        
                                        <div>
                                            <Label>Nume</Label>
                                            <Input
                                                value={customerName}
                                                onChange={(e) => setCustomerName(e.target.value)}
                                                placeholder="Nume client"
                                            />
                                        </div>
                                        <div>
                                            <Label>Telefon</Label>
                                            <Input
                                                value={customerPhone}
                                                onChange={(e) => setCustomerPhone(e.target.value)}
                                                placeholder="Număr telefon"
                                            />
                                        </div>
                                        <div>
                                            <Label>Adresă</Label>
                                            <Input
                                                value={customerAddress}
                                                onChange={(e) => setCustomerAddress(e.target.value)}
                                                placeholder="Adresă livrare"
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    if (showNewCustomerForm && !existingCustomer) {
                                        setShowNewCustomerForm(false);
                                    } else {
                                        setStep(2);
                                        setDestination(null);
                                    }
                                }}
                                className="flex-1"
                            >
                                Înapoi
                            </Button>
                            <Button 
                                onClick={handleComplete}
                                disabled={loading || !customerId.trim()}
                                className="flex-1 bg-rose-500 hover:bg-rose-600"
                            >
                                {loading ? 'Se procesează...' : 'Finalizează'}
                            </Button>
                        </div>
                    </div>
                ) : step === 3 && destination === 'depozit' ? (
                    /* Step 3: Depozit Location */
                    <div className="space-y-4">
                        <div className="bg-zinc-50 rounded-lg p-3">
                            <p className="text-sm text-zinc-500">Produs:</p>
                            <p className="font-semibold">{productName} x{quantity}</p>
                        </div>
                        
                        <div className="flex items-center gap-2 text-blue-600 bg-blue-50 rounded-lg p-3">
                            <Warehouse size={18} />
                            <span className="text-sm font-medium">Destinație: Depozit</span>
                        </div>
                        
                        <div>
                            <Label>Locație *</Label>
                            <Input
                                value={depozitLocation}
                                onChange={(e) => setDepozitLocation(e.target.value.toUpperCase())}
                                placeholder="P01, P02, etc."
                            />
                        </div>
                        
                        {existingLocations.length > 0 && (
                            <div>
                                <Label className="text-xs text-zinc-500">Locații existente:</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {existingLocations.map(loc => (
                                        <button
                                            key={loc}
                                            type="button"
                                            onClick={() => setDepozitLocation(loc)}
                                            className={`px-3 py-1 text-sm rounded-full border ${
                                                depozitLocation === loc 
                                                    ? 'bg-blue-500 text-white border-blue-500' 
                                                    : 'bg-white text-zinc-700 border-zinc-300 hover:border-blue-300'
                                            }`}
                                        >
                                            {loc}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setStep(2);
                                    setDestination(null);
                                }}
                                className="flex-1"
                            >
                                Înapoi
                            </Button>
                            <Button 
                                onClick={handleComplete}
                                disabled={loading || !depozitLocation.trim()}
                                className="flex-1 bg-blue-500 hover:bg-blue-600"
                            >
                                {loading ? 'Se procesează...' : 'Pune în Depozit'}
                            </Button>
                        </div>
                    </div>
                ) : step === 3 && destination === 'magazin' ? (
                    /* Step 3: Magazin Confirmation */
                    <div className="space-y-4">
                        <div className="bg-zinc-50 rounded-lg p-3">
                            <p className="text-sm text-zinc-500">Produs:</p>
                            <p className="font-semibold">{productName} x{quantity}</p>
                        </div>
                        
                        <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-lg p-3">
                            <Store size={18} />
                            <span className="text-sm font-medium">Destinație: Magazin</span>
                        </div>
                        
                        <p className="text-sm text-center text-zinc-600">
                            Produsul va fi adăugat în stocul magazinului, disponibil pentru vânzare directă.
                        </p>
                        
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setStep(2);
                                    setDestination(null);
                                }}
                                className="flex-1"
                            >
                                Înapoi
                            </Button>
                            <Button 
                                onClick={handleComplete}
                                disabled={loading}
                                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                            >
                                {loading ? 'Se procesează...' : 'Pune în Magazin'}
                            </Button>
                        </div>
                    </div>
                ) : null}
            </DialogContent>
            
            {/* Camera Scanner Modal */}
            <BarcodeScanner
                isOpen={showCameraScanner}
                onClose={() => setShowCameraScanner(false)}
                onScan={handleCameraScan}
            />
        </Dialog>
    );
};

export default QuickSaleModal;
